import type { ExamPrescriptionItemInput } from "@/lib/validations/exam-prescription";
import type { ExamPrescriptionTemplateWithItems } from "@/hooks/useExamPrescriptionTemplates";

export type DuplicateResolution = "all" | "unique";

export interface ExpandedTemplateData {
  itemsToAdd: ExamPrescriptionItemInput[];
  nextObservations: string;
}

export interface TemplateDuplicateConflict {
  templateId: string;
  templateTitle: string;
  duplicateExamNames: string[];
  expanded: ExpandedTemplateData;
}

const normalizeExamName = (value: string) =>
  value
    .trim()
    .toLocaleLowerCase()
    .replace(/\s+/g, " ");

export const buildDefaultExamPrescriptionItem = (): ExamPrescriptionItemInput => ({
  exam_type_id: "",
  exam_name: "",
  instructions: "",
});

export const mapTemplateToPrescriptionItems = (
  template: ExamPrescriptionTemplateWithItems,
): ExamPrescriptionItemInput[] =>
  template.items.map((item) => ({
    exam_type_id: item.exam_type_id ?? "",
    exam_name: item.exam_name ?? "",
    instructions: item.instructions ?? "",
  }));

export const appendTemplateObservations = (
  currentObservations: string | undefined,
  templateObservations: string | null | undefined,
): string => {
  const current = currentObservations?.trim() ?? "";
  const incoming = templateObservations?.trim() ?? "";

  if (!incoming) return currentObservations ?? "";
  if (!current) return incoming;

  return `${current}\n\n${incoming}`;
};

export const getTemplateDuplicateConflict = ({
  template,
  currentItems,
  currentObservations,
}: {
  template: ExamPrescriptionTemplateWithItems;
  currentItems: ExamPrescriptionItemInput[];
  currentObservations?: string;
}): TemplateDuplicateConflict | null => {
  const expandedItems = mapTemplateToPrescriptionItems(template);
  const existingNames = new Set(
    currentItems
      .map((item) => normalizeExamName(item.exam_name ?? ""))
      .filter(Boolean),
  );

  const duplicateExamNames = expandedItems
    .filter((item) => existingNames.has(normalizeExamName(item.exam_name ?? "")))
    .map((item) => item.exam_name.trim())
    .filter(Boolean)
    .filter((value, index, list) => list.findIndex((entry) => normalizeExamName(entry) === normalizeExamName(value)) === index);

  if (duplicateExamNames.length === 0) return null;

  return {
    templateId: template.id,
    templateTitle: template.title,
    duplicateExamNames,
    expanded: {
      itemsToAdd: expandedItems,
      nextObservations: appendTemplateObservations(currentObservations, template.observations),
    },
  };
};

export const expandTemplateIntoPrescription = ({
  template,
  currentItems,
  currentObservations,
  duplicateResolution = "all",
}: {
  template: ExamPrescriptionTemplateWithItems;
  currentItems: ExamPrescriptionItemInput[];
  currentObservations?: string;
  duplicateResolution?: DuplicateResolution;
}): ExpandedTemplateData => {
  const expandedItems = mapTemplateToPrescriptionItems(template);

  const itemsToAdd =
    duplicateResolution === "unique"
      ? (() => {
          const existingNames = new Set(
            currentItems
              .map((item) => normalizeExamName(item.exam_name ?? ""))
              .filter(Boolean),
          );

          return expandedItems.filter((item) => !existingNames.has(normalizeExamName(item.exam_name ?? "")));
        })()
      : expandedItems;

  return {
    itemsToAdd,
    nextObservations: appendTemplateObservations(currentObservations, template.observations),
  };
};
